// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, IconButton, Button } from '@material-ui/core';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import ReactPlayer from 'react-player';
import captureVideoFrame from 'capture-video-frame';

import CloseIcon from '@material-ui/icons/Close';
import CropIcon from '@material-ui/icons/Crop';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

// Internal Dependencies
import SortableVideoItem from './SortableVideoItem';
import FixedCropper from '../image/FixedCropper';
import { IsEmpty } from '../../../common/checks';
import { ImageType } from '../../../common/constants';
import { Sleep, getThumbnailFromVideoURL } from '../../../common/util';
import apiService from '../../../services.js/apiService';

let player;
const SortableVideo = ({ videos, reviewId, postId, onSort, onDelete, setThumbnailAsImage }) => {
  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(MouseSensor),
  );

  const [items, setItems] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [capturedThumbnail, setCapturedThumbnail] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [hasThumbnail, setHasThumbnail] = useState(false);
  const [hasSeek, setHasSeek] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [src, setSrc] = useState(null);
  const [thumbnailURL, setThumbnailURL] = useState(null);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (IsEmpty(active) || IsEmpty(over)) {
      return;
    }

    if (active.id !== over.id) {
      setItems((_items) => {
        const oldIndex = _items.findIndex((item) => item.id === active.id);
        const newIndex = _items.findIndex((item) => item.id === over.id);

        const sorted = arrayMove(_items, oldIndex, newIndex);
        const sortedVideo = _.map(sorted, (item) => item.video);
        onSort(sortedVideo);

        return sorted;
      });
    }
  };

  const handleCloseCropper = () => {
    setShowCropper(false);
    setSrc(null);
    setShowVideo(true);
  };

  const handleClose = () => {
    setShowVideo(false);
    setCapturedThumbnail(null);
    setHasSeek(false);
    setThumbnailFile(null);
    setThumbnailURL(null);
    setHasThumbnail(false);
    setSrc(null);
    setShowCropper(false);
  };

  const onUploadThumbnail = async (file) => {
    if (file) {
      setThumbnailURL(null);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('imageType', ImageType.VideoThumbnail);
      let cloudfrontLink;

      if (reviewId) {
        cloudfrontLink = await apiService.uploadReviewMedia(reviewId, formData, file);
      } else {
        cloudfrontLink = await apiService.uploadPostMedia(postId, formData, file);
      }

      setThumbnailURL(`${cloudfrontLink}?${Math.random()}`);
      setShowCropper(false);
      setCapturedThumbnail(null);
      setHasThumbnail(true);
      setShowVideo(true);

      await Sleep(200);
      setThumbnailAsImage(cloudfrontLink);
    }
  };

  const onCaptureThumbnail = () => {
    const frame = captureVideoFrame(player.getInternalPlayer());
    setCapturedThumbnail(frame);
    setThumbnailURL(frame.dataUri);
    setHasThumbnail(true);
  };

  const hasThumbnailFunc = (value) => {
    const image = document.createElement('img');

    image.onload = () => {
      setHasThumbnail(true);
      return true;
    };
    image.onerror = () => {
      setHasThumbnail(false);
      return false;
    };
    image.src = `${getThumbnailFromVideoURL(value)}?${Math.random()}`;
  };

  const handleSaveThumbnail = async () => {
    const file = new File(
      [capturedThumbnail.blob],
      `${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.jpg`,
      { type: 'image/jpeg' },
    );

    if (file) {
      setShowCropper(true);
      setShowVideo(false);
      setThumbnailFile(file);

      await Sleep(200);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSrc(reader.result);
        setHasThumbnail(false);
      });
      reader.readAsDataURL(file);
    }
  };

  const imageExists = (imageURL) => {
    const http = new XMLHttpRequest();
    try {
      http.open('HEAD', `${imageURL}?${Math.random()}`, false);
      http.send();

      return http.status === 200;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const copyVideos = _.cloneDeep(videos);
    const formattedVideos = _.map(copyVideos, (video, index) => ({ id: `${new Date().getTime()}-index-${index + 1}`, video }));

    setItems(formattedVideos);
  }, [videos]);

  return (
    <div>
      <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext
          items={items}
          strategy={rectSortingStrategy}
        >
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '20px', marginTop: '50px', marginBottom: '20px' }}>
            {items.map((item) => (
              <SortableVideoItem
                key={item.id}
                id={item.id}
                value={item.video}
                showVideo={showVideo}
                imageExists={imageExists(getThumbnailFromVideoURL(item.video))}
              >
                <div style={{ position: 'relative' }}>
                  <div className="row" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', position: 'absolute', bottom: '-19.5rem', right: '2rem', width: '120px', zIndex: 10 }}>
                    <IconButton
                      onClick={() => {
                      setVideoUrl(item.video);
                      setShowVideo(true);
                      setThumbnailURL(`${getThumbnailFromVideoURL(item.video)}?${Math.random()}`);
                      hasThumbnailFunc(item.video);
                      }}
                      style={{
                        backgroundColor: '#ffffff',
                        boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                        verticalAlign:   'bottom',
                        margin:          '10px 5px 0px 0px',
                      }}
                    >
                      <CropIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => onDelete(item.video)}
                      style={{
                        backgroundColor: '#ffffff',
                        boxShadow:       '0 1px 2px 1px rgba(0, 0, 0, .1)',
                        verticalAlign:   'bottom',
                        margin:          '10px 15px 0px 0px',
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                </div>
              </SortableVideoItem>
            )) }
          </div>
        </SortableContext>
      </DndContext>

      <Dialog
        id="videoDialog"
        fullWidth
        maxWidth="md"
        open={showVideo}
        onClose={handleClose}
      >
        <DialogTitle disableTypography style={{ height: 0, marginBottom: -20 }}>
          <IconButton
            onClick={handleClose}
            style={{ float: 'right', marginTop: -20, marginRight: -20 }}
          >
            <CloseIcon style={{ height: 15, width: 15 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 2fr)', gridColumnGap: capturedThumbnail ? 50 : 120, margin: 10, marginLeft: 30 }}>
            <div style={{ textAlign: 'center' }}>
              <ReactPlayer
                ref={(ref) => { player = ref; }}
                url={videoUrl}
                playing={!hasSeek}
                controls
                width={400}
                height={250}
                onSeek={() => setHasSeek(true)}
                volume={0}
                config={{
                  file: {
                    attributes: {
                      crossOrigin: 'anonymous',
                    },
                  },
                }}
              />
              <Button
                onClick={onCaptureThumbnail}
                style={{ width: 225, marginTop: 10, marginBottom: 10 }}
                variant="contained"
                color="secondary"
              >
                Capture frame as thumbnail
              </Button>
            </div>
            {hasThumbnail
              ? (
                <div style={{ textAlign: 'center' }}>
                  <img src={thumbnailURL} style={{ marginTop: 12, maxWidth: capturedThumbnail ? 405 : 280 }} alt="" />
                  {capturedThumbnail
                    ? (
                      <Button
                        onClick={handleSaveThumbnail}
                        style={{ width: 225, marginTop: 20, marginBottom: 10 }}
                        variant="contained"
                        color="secondary"
                      >
                        Crop & Save Thumbnail
                      </Button>
                    ) : null}
                </div>
              )
              : (
                <ReactPlayer
                  url={videoUrl}
                  width={400}
                  height={250}
                  style={{ marginLeft: -70 }}
                  config={{
                    file: {
                      attributes: {
                        crossOrigin: 'anonymous',
                      },
                    },
                  }}
                />
              )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullScreen
        open={showCropper}
        onClose={handleCloseCropper}
      >
        <FixedCropper
          width={4}
          height={3}
          cropPixelWidth={400}
          cropPixelHeight={400}
          outputPixelWidth={900}
          outputPixelHeight={900}
          type="upload"
          src={src}
          file={thumbnailFile}
          fileName={`${(videoUrl.split('/')[videoUrl.split('/').length - 1]).split('.')[0]}.jpg`}
          handleSave={onUploadThumbnail}
          handleClose={handleCloseCropper}
          isCropping
        />
      </Dialog>
    </div>
  );
};

export default connect(null, { })(SortableVideo);

SortableVideo.propTypes = {
  videos:   PropTypes.arrayOf(PropTypes.string),
  reviewId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  postId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSort:              PropTypes.func,
  onDelete:            PropTypes.func,
  setThumbnailAsImage: PropTypes.func,
};

SortableVideo.defaultProps = {
  videos:              [],
  reviewId:            null,
  postId:              null,
  onSort:              () => {},
  onDelete:            () => {},
  setThumbnailAsImage: () => {},
};
